"use client";

import { useEffect, useState, type MouseEvent, type JSX } from "react";
import { useLenis } from "@/components/providers/SmoothScrollProvider";
import { CONSULTATION_URL, SPECIALIST_URL } from "@/lib/consultation-links";
import { ctaButtonClassName } from "@/lib/cta-styles";
import { setStickyCtaVisibility } from "@/lib/floating-cta-visibility";
import { scrollToHash } from "@/lib/scroll-to-hash";
import { cn } from "@/lib/utils";

const HERO_ID = "hero-cta-buttons";

interface StickyBottomCtaProps {
  /** Defaults mirror home-v3's hero — the only page with no CMS content to pull from. */
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}

export const StickyBottomCta = ({
  primaryLabel = "Book a Consultation Call",
  primaryHref = CONSULTATION_URL,
  secondaryLabel = "Talk to a Specialist",
  secondaryHref = SPECIALIST_URL,
}: StickyBottomCtaProps = {}): JSX.Element => {
  const lenisRef = useLenis();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById(HERO_ID);

    // Pages with a hero section: show once it's scrolled fully out of view.
    if (hero) {
      const observer = new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0),
        { threshold: 0 },
      );
      observer.observe(hero);
      return () => observer.disconnect();
    }

    // Pages without one: fall back to scrolling past the first screenful.
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.9);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Published so the chat nudge bubble can sit above this bar only while it is
  // actually on screen.
  useEffect(() => {
    setStickyCtaVisibility(visible);
  }, [visible]);

  // Pages that never render this bar must not inherit a stale "visible".
  useEffect(() => () => setStickyCtaVisibility(false), []);

  return (
    <div
      aria-hidden={!visible}
      className={`chat-safe-floating-cta fixed inset-x-0 z-40 flex justify-center px-4 transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <div className="flex flex-nowrap items-center justify-center gap-2 sm:gap-3 rounded-[40px] border border-white/10 bg-[#0f1218]/95 p-2 shadow-[0px_8px_24px_rgba(0,0,0,0.5)] backdrop-blur-sm">
        {[
          { label: secondaryLabel, href: secondaryHref, variant: "secondary" as const },
          { label: primaryLabel, href: primaryHref, variant: "primary" as const },
        ].map(({ label, href, variant }) => {
          // In-page anchors (e.g. "#pricing") scroll via a slower, explicit
          // Lenis animation and must never open in a new tab.
          const isAnchor = href.startsWith("#");

          return (
            <a
              key={variant}
              href={href}
              {...(isAnchor
                ? {
                    onClick: (event: MouseEvent) => {
                      event.preventDefault();
                      // Stops Lenis's own global anchor-click handler
                      // (anchors: true, on window) from also firing and
                      // restarting the scroll at its default duration,
                      // clobbering the slower one below.
                      event.stopPropagation();
                      scrollToHash(lenisRef.current, href);
                    },
                  }
                : { target: "_blank", rel: "noopener noreferrer" })}
              tabIndex={visible ? 0 : -1}
              className={cn(ctaButtonClassName(variant), "px-3 py-2.5 sm:px-5 sm:py-4")}
            >
              {label}
            </a>
          );
        })}
      </div>
    </div>
  );
};
